/**
 * Cross-tab IAM profile sync — no tokens or profile payloads in messages.
 */

export type IamProfileSyncEventType =
  | 'iam-profile-cleared'
  | 'iam-profile-refreshed'
  | 'auth-logout'
  | 'auth-token-refresh-failed';

export type IamProfileSyncPayload = {
  type: IamProfileSyncEventType;
  ts: number;
  policyVersion?: number | null;
};

const CHANNEL_NAME = 'march-iam-profile-sync';
const STORAGE_KEY = 'march_iam_profile_sync';

function publishSyncEvent(payload: IamProfileSyncPayload): void {
  if (typeof window === 'undefined') return;
  try {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.postMessage(payload);
    channel.close();
  } catch {
    // BroadcastChannel unavailable.
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage failures.
  }
}

export function broadcastIamProfileCleared(): void {
  publishSyncEvent({ type: 'iam-profile-cleared', ts: Date.now(), policyVersion: null });
}

export function broadcastIamProfileRefreshed(policyVersion: number | null): void {
  publishSyncEvent({ type: 'iam-profile-refreshed', ts: Date.now(), policyVersion });
}

export function broadcastIamAuthLogout(): void {
  publishSyncEvent({ type: 'auth-logout', ts: Date.now(), policyVersion: null });
}

export function broadcastIamTokenRefreshFailed(): void {
  publishSyncEvent({ type: 'auth-token-refresh-failed', ts: Date.now(), policyVersion: null });
}

export function subscribeIamProfileSyncEvents(
  handler: (payload: IamProfileSyncPayload) => void,
): () => void {
  if (typeof window === 'undefined') return () => undefined;

  let channel: BroadcastChannel | null = null;
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (event: MessageEvent<IamProfileSyncPayload>) => {
      if (event.data?.type) handler(event.data);
    };
  } catch {
    // Ignore.
  }

  const onStorage = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY || !event.newValue) return;
    try {
      const payload = JSON.parse(event.newValue) as IamProfileSyncPayload;
      if (payload?.type) handler(payload);
    } catch {
      // Ignore malformed sync payloads.
    }
  };
  window.addEventListener('storage', onStorage);

  return () => {
    if (channel) channel.close();
    window.removeEventListener('storage', onStorage);
  };
}
